import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import axios from "axios";
import {
  Sparkles,
  ChevronLeft,
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import Header from "../components/Header";
import BetaGatedModal from "../components/BetaGatedModal";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { safeLocal } from "../lib/safeStorage";
import { PENDING_TOS_VERSION_KEY } from "../lib/storageKeys";
import { trackFP } from "../lib/firstPartyAnalytics";
import { buildAcquisitionPayload } from "../lib/utm";
import { startGoogleLogin } from "../lib/googleAuth";
import { newTrialSignupDestination } from "../lib/trialSignup";
import { getSignupRecaptchaToken } from "../lib/recaptcha";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// FastAPI returns `detail` as a string for HTTPException but as an
// array of `{type, loc, msg, input, ctx}` objects for 422 validation
// errors. Sonner/React can't render an array of objects as children, so
// we coerce it to a human-friendly string before calling toast.error().
const formatApiError = (err, fallback) => {
  const raw = err?.response?.data?.detail;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw)) {
    const joined = raw
      .map((d) => (typeof d === "string" ? d : d?.msg))
      .filter(Boolean)
      .join("; ");
    if (joined) return joined;
  }
  return fallback;
};

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    <path fill="none" d="M0 0h48v48H0z" />
  </svg>
);

const Login = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, setUser } = useAuth();

  // Public demo mode (demo.trubeaute.com) — sign-in is intentionally
  // disabled so demo activity never attaches to a real account. Anyone
  // reaching /login in demo posture is redirected home.
  useEffect(() => {
    if (typeof window !== "undefined" && window.__trubeaute_demo_mode) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // ── Mode state ────────────────────────────────────────────────
  // authMode: which credential flow the user is using right now
  //   "password-signin" | "password-signup" | "magic-link"
  // view: the screen in this flow: "idle" | "sending" | "sent"
  const [authMode, setAuthMode] = useState("password-signin");
  const [view, setView] = useState("idle");
  // Email options stay hidden by default — the primary CTA is
  // "Continue with Google". Click "Use email instead" to reveal the
  // password/magic-link tabs. Reduces visible decision surface by
  // ~60% for the TikTok-driven funnel where most users will Google-
  // auth anyway. Auto-expands when arriving via `?signup=1`.
  const [showEmailOptions, setShowEmailOptions] = useState(false);

  // Shared form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Marketing opt-in: explicit, default OFF so the user actively
  // consents. Stored on the user row so CAN-SPAM / GDPR can verify
  // legitimate basis when we send product-update emails. Without this,
  // sending broadcasts to "all users" is legally murky.
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [tosVersion, setTosVersion] = useState(null);

  // Private-beta gate. When the backend rejects an auth attempt with
  // 403 + `{detail: {error: "beta_gated", title, message, cta_label}}`
  // we open this modal instead of showing a generic toast.
  const [betaGate, setBetaGate] = useState({ open: false, payload: null });
  const tryHandleBetaGate = (err) => {
    const detail = err?.response?.data?.detail;
    if (
      err?.response?.status === 403 &&
      detail &&
      typeof detail === "object" &&
      detail.error === "beta_gated"
    ) {
      setBetaGate({ open: true, payload: detail });
      return true;
    }
    return false;
  };

  // Auto-open the beta-gate modal when the Google OAuth callback
  // bounced here with `?beta_gated=1`. We can't reach the original
  // server payload from a redirect, so we render the canonical
  // founder-written copy by passing `null` payload and letting
  // BetaGatedModal use its defaults.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("beta_gated") === "1") {
      setBetaGate({ open: true, payload: null });
      // Clear the flag from the URL so a refresh doesn't re-open it.
      const url = new URL(window.location.href);
      url.searchParams.delete("beta_gated");
      window.history.replaceState({}, "", url.toString());
    }
    // Honour `?signup=1` from the landing-page "Create your free account"
    // CTA so first-time visitors land directly on the sign-up tab. The
    // signin tab is still one click away if they're actually returning.
    // Also auto-expands the email options so the form is immediately
    // visible — the user already declared intent by clicking
    // "Create account" upstream.
    if (params.get("signup") === "1") {
      setAuthMode("password-signup");
      setShowEmailOptions(true);
      const url = new URL(window.location.href);
      url.searchParams.delete("signup");
      window.history.replaceState({}, "", url.toString());
      // First-party funnel: declared signup intent (vs. login intent)
      trackFP("signup_started", { source: "url_param" });
    }
  }, []);

  // ── Resolve the post-auth destination ─────────────────────────
  // Honor a `?next=...` query param when present so upstream surfaces
  // (HeroTrouveChat, deep-link share URLs, /pro/claim retry, etc.)
  // can route the user back to where they came from. Sanitized to
  // prevent open-redirect: the value MUST be a same-origin path that
  // starts with a single `/` (rejects `//evil.com`, `http://...`,
  // empty strings). Default destination remains `/home` — the warm
  // "what's the vibe today?" landing.
  const resolveNextDest = useCallback(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const raw = params.get("next");
      if (!raw) return "/home";
      if (typeof raw !== "string") return "/home";
      if (!raw.startsWith("/")) return "/home";
      if (raw.startsWith("//")) return "/home";
      return raw;
    } catch {
      return "/home";
    }
  }, [location.search]);

  // ── Already signed in? bounce to the resolved destination ─────
  useEffect(() => {
    if (!loading && user) {
      navigate(resolveNextDest(), { replace: true });
    }
  }, [loading, user, navigate, resolveNextDest]);

  // ── ToS version fetch ─────────────────────────────────────────
  // /api/legal/tos-version is PUBLIC (no auth) so the Login page —
  // which by definition is unauth'd — can stamp the real version
  // string on new sign-ups instead of the "unknown" fallback. The
  // authed /api/me/tos endpoint stays for the in-app re-accept flow.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/legal/tos-version`);
        if (!cancelled) setTosVersion(data?.current_version || null);
      } catch {
        if (!cancelled) setTosVersion("unknown");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Handlers ──────────────────────────────────────────────────
  // No explicit checkbox gates — the act of clicking any auth button
  // is the affirmative consent. The inline legal copy below the
  // buttons names what they're agreeing to (Terms, Privacy, age 13+).
  // Equivalent to E-SIGN-act "clickwrap" + GDPR Art.7 "clear
  // affirmative action" as long as the legal copy is visible AND the
  // linked Terms / Privacy pages are accessible — both verified.
  const handleGoogleLogin = () => {
    safeLocal.setItem(PENDING_TOS_VERSION_KEY, tosVersion || "unknown");
    // White-labeled Google OAuth ("Sign in to TruBeauté") via our own
    // Google Cloud client — lands back on /auth/google for the exchange.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    //
    // The return path defaults to /my-routines (Login.jsx convention)
    // but honors ?next= when present — same chokepoint as the
    // password handlers, so HeroTrouveChat → Google signup keeps the
    // hero intent → /onboarding flow intact. resolveNextDest() returns
    // "/home" as its default; we override that to "/my-routines"
    // ONLY when ?next= is absent (preserves the prior behaviour for
    // visitors who land on /login with no intent in tow).
    const nextDest = resolveNextDest();
    startGoogleLogin(nextDest === "/home" ? "/my-routines" : nextDest);
  };

  const handlePasswordSignIn = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!email.includes("@") || password.length < 8) {
      toast.error(t("login.toastEnterEmailPass"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/password/login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true }
      );
      setUser(data);
      // Hard replace so the cookie is authoritative going forward.
      // Honor ?next= via resolveNextDest() so upstream surfaces
      // (HeroTrouveChat, deep-link share URLs, /pro/claim retry) can
      // route the user back to where they came from. Falls back to
      // /home when ?next is absent or unsafe (open-redirect guard).
      window.location.replace(resolveNextDest());
    } catch (err) {
      if (tryHandleBetaGate(err)) {
        setSubmitting(false);
        return;
      }
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(formatApiError(err, t("login.toastRateLimit")));
      } else {
        toast.error(formatApiError(err, t("login.toastSigninFailed")));
      }
      setSubmitting(false);
    }
  };

  const handlePasswordSignUp = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t("login.toastChooseName"));
      return;
    }
    if (submitting) return;
    if (!email.includes("@")) {
      toast.error(t("login.toastValidEmail"));
      return;
    }
    if (password.length < 8) {
      toast.error(t("login.toastPasswordLength"));
      return;
    }
    setSubmitting(true);
    try {
      // Read the active UTM campaign so we can tag this signup with
      // its source campaign. When utm_campaign === "pride_2026" the
      // backend gates a 7-day Pride claim window in the user record,
      // which surfaces the in-app PrideClaimBanner.
      let signupCampaign = undefined;
      try {
        const utm = JSON.parse(
          sessionStorage.getItem("trubeaute.utm.v1") || "{}",
        );
        if (typeof utm.utm_campaign === "string" && utm.utm_campaign) {
          signupCampaign = utm.utm_campaign.slice(0, 40);
        }
      } catch {
        // sessionStorage disabled or corrupted — skip campaign tag
      }
      // Execute on submit because v3 tokens are short-lived and single-use.
      const recaptchaToken = await getSignupRecaptchaToken();
      const { data } = await axios.post(
        `${API}/auth/password/register`,
        {
          email: email.trim().toLowerCase(),
          password,
          name: name.trim() || undefined,
          tos_version: tosVersion || "unknown",
          origin: window.location.origin,
          locale: i18n.language?.split("-")[0] || "en",
          marketing_opt_in: marketingOptIn,
          campaign: signupCampaign,
          acquisition: buildAcquisitionPayload() || undefined,
          recaptcha_token: recaptchaToken,
        },
        { withCredentials: true }
      );
      setUser(data);
      // First-party funnel: signup_completed (fires once per successful
      // password-register response — magic link / OAuth completions are
      // tracked separately in their own callback handlers)
      trackFP("signup_completed", { method: "password" });
      window.location.replace(newTrialSignupDestination(resolveNextDest()));
    } catch (err) {
      if (tryHandleBetaGate(err)) {
        setSubmitting(false);
        return;
      }
      const status = err?.response?.status;
      if (status === 409) {
        toast.error(
          formatApiError(err, t("login.toastAccountExists"))
        );
        setAuthMode("password-signin");
      } else {
        toast.error(
          formatApiError(err, t("login.toastSignupFailed"))
        );
      }
      setSubmitting(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t("login.toastChooseName"));
      return;
    }
    if (!email || !email.includes("@")) {
      toast.error(t("login.toastEnterValidEmail"));
      return;
    }
    setView("sending");
    try {
      const recaptchaToken = await getSignupRecaptchaToken();
      await axios.post(`${API}/auth/magic-link/request`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        origin: window.location.origin,
        tos_version: tosVersion || "unknown",
        locale: i18n.language?.split("-")[0] || "en",
        marketing_opt_in: marketingOptIn,
        // Preserve a selected Monthly/Annual trial through the email
        // round-trip. Backend sanitizes this path both when the link is
        // created and when it is redeemed.
        next_path: resolveNextDest(),
        // Acquisition fingerprint carries through the email round-trip
        // — re-attached to the user doc on the magic-link verify step.
        acquisition: buildAcquisitionPayload() || undefined,
        recaptcha_token: recaptchaToken,
      });
      setSentTo(email.trim().toLowerCase());
      setView("sent");
    } catch (err) {
      if (tryHandleBetaGate(err)) {
        setView("idle");
        return;
      }
      toast.error(
        formatApiError(err, t("login.toastMagicFailed"))
      );
      setView("idle");
    }
  };

  // ── Tab button ────────────────────────────────────────────────
  const ModeTab = ({ id, label, testid }) => {
    const active = authMode === id;
    return (
      <button
        type="button"
        onClick={() => {
          setAuthMode(id);
          setView("idle");
          setPassword("");
        }}
        className={
          active
            ? "flex-1 px-3 py-2 rounded-full bg-white text-[#2C2123] text-xs font-medium border border-[#EADDD7] shadow-sm transition-all"
            : "flex-1 px-3 py-2 rounded-full bg-transparent text-[#8A7D82] text-xs hover:text-[#2C2123] transition-colors"
        }
        data-testid={testid}
      >
        {label}
      </button>
    );
  };

  const inputClass =
    "w-full bg-white border border-[#EADDD7] rounded-full pl-10 pr-4 py-3 text-[#2C2123] placeholder:text-[#8A7D82] focus:outline-none focus:ring-2 focus:ring-[#D5A8A4]/50 focus:border-[#D5A8A4] transition-all disabled:opacity-50";

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <div className="hidden sm:block"><Header /></div>
      <Link
        to="/"
        className="sm:hidden absolute top-4 left-5 flex items-center gap-2 font-serif text-xl text-[#2C2123]"
        aria-label="TruBeauté home"
      >
        <span className="h-8 w-8 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center shadow-[0_4px_12px_rgba(155,106,108,0.28)]">
          <Sparkles className="h-4 w-4 text-white" />
        </span>
        <span>TruBeauté</span>
      </Link>

      <section
        className="pt-16 sm:pt-32 pb-8 sm:pb-24 px-3 sm:px-6 flex flex-col items-center justify-start min-h-[calc(100vh-72px)]"
        data-testid="login-root"
      >
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-md w-full"
        >
          {/* Soft beta banner — flagged so we can hide it once we're
              out of beta without touching the rest of the page. */}
          <div
            className="hidden sm:flex mb-5 rounded-2xl bg-gradient-to-br from-[#F5DCD7] to-[#EAC9C2] border border-[#D5A8A4]/50 px-5 py-4 items-start gap-3 soft-shadow"
            data-testid="login-beta-banner"
          >
            <span className="inline-flex h-7 w-7 rounded-full bg-white/40 backdrop-blur-sm items-center justify-center shrink-0 mt-0.5">
              <Sparkles className="h-3.5 w-3.5 text-[#9B6A6C]" />
            </span>
            <div className="text-sm leading-relaxed text-[#5C4B51]">
              <span className="font-medium text-[#2C2123]">{t("login.betaTitle")}</span>{" "}
              {t("login.betaBody")}
            </div>
          </div>

          <div className="bg-white border border-[#EADDD7] rounded-2xl sm:rounded-3xl p-5 sm:p-10 soft-shadow-lg">
            <div className="hidden sm:flex h-14 w-14 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] items-center justify-center mb-6 soft-shadow">
              <Sparkles className="h-6 w-6 text-white" />
            </div>

            <AnimatePresence mode="wait">
              {view === "sent" ? (
                <motion.div
                  key="sent"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4 }}
                  data-testid="login-sent-state"
                >
                  <span className="hidden sm:inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
                    {t("login.sentKicker")}
                  </span>
                  <h1 className="font-serif italic text-3xl sm:text-4xl text-[#2C2123] leading-tight mb-3">
                    <Trans
                      i18nKey="login.sentHeadline"
                      components={{ br: <br /> }}
                    />
                  </h1>
                  <p className="text-[#5C4B51] leading-relaxed mb-6">
                    <Trans
                      i18nKey="login.sentBodyHtml"
                      values={{ email: sentTo }}
                      components={{ strong: <strong className="text-[#2C2123]" /> }}
                    />
                  </p>
                  <div className="bg-[#F5EFEB] rounded-2xl p-4 flex items-start gap-3 mb-6">
                    <CheckCircle2 className="h-5 w-5 text-[#9B6A6C] shrink-0 mt-0.5" />
                    <p className="text-sm text-[#5C4B51] leading-relaxed">
                      <Trans
                        i18nKey="login.linkExpiryHtml"
                        components={{ strong: <strong /> }}
                      />
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setView("idle");
                      setEmail("");
                    }}
                    className="text-sm text-[#9B6A6C] hover:text-[#805557] transition-colors"
                    data-testid="login-send-another-btn"
                  >
                    {t("login.useDifferentEmail")}
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4 }}
                >
                  <span className="hidden sm:inline-block text-xs uppercase tracking-[0.25em] text-[#9B6A6C] mb-2">
                    {authMode === "password-signup"
                      ? t("login.kickerSignUp")
                      : t("login.kickerSignIn")}
                  </span>
                  <h1
                    className="font-serif italic text-3xl sm:text-4xl text-[#2C2123] leading-tight mb-3"
                    data-testid="login-headline"
                  >
                    {/* Translation contains a <br/> structural tag.
                        `<Trans>` whitelists the elements the JSON is
                        allowed to render — keeps the typography
                        without `dangerouslySetInnerHTML`. */}
                    <span className="sm:hidden">{t("login.mobileHeadline")}</span>
                    <span className="hidden sm:block">
                      <Trans i18nKey="login.headline" components={{ br: <br /> }} />
                    </span>
                  </h1>
                  <p className="text-sm sm:text-base text-[#5C4B51] mb-4 sm:mb-6 leading-relaxed">
                    {authMode === "password-signup"
                      ? t("login.sublineSignUp")
                      : t("login.sublineSignIn")}
                  </p>

                  {/* Google first — biggest CTA. No gate check; the
                      inline legal copy below the buttons IS the consent. */}
                  <button
                    onClick={handleGoogleLogin}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full px-6 py-3 sm:py-4 text-base transition-all duration-300 soft-shadow bg-white border border-[#EADDD7] text-[#2C2123] hover:border-[#9B6A6C] hover:-translate-y-0.5"
                    data-testid="login-google-btn"
                  >
                    <GoogleIcon />
                    {t("login.continueWithGoogle")}
                  </button>

                  {/* Email option — sits directly below Google as a
                      first-class peer. Founder-reported (iter 75, Jun
                      24 2026): the old text-link version made email
                      sign-in look like an after-thought. Now it's a
                      full-width outlined button mirroring Google's
                      shape, so returning users who don't use Google
                      OAuth can find their account immediately. When
                      collapsed (default), one click expands the
                      tabs + password / magic-link form below. */}
                  {!showEmailOptions && (
                    <button
                      type="button"
                      onClick={() => setShowEmailOptions(true)}
                      className="mt-3 w-full inline-flex items-center justify-center gap-3 rounded-full px-6 py-4 text-base transition-all duration-300 soft-shadow bg-white border border-[#EADDD7] text-[#2C2123] hover:border-[#9B6A6C] hover:-translate-y-0.5"
                      data-testid="login-show-email-btn"
                    >
                      <Mail className="h-4 w-4 text-[#9B6A6C]" />
                      {t("login.continueWithEmail")}
                    </button>
                  )}

                  {/* Inline consent — clicking ANY auth button above
                      or below is the affirmative act. The linked Terms
                      / Privacy pages are accessible from this same
                      paragraph, satisfying GDPR Art.7 "clear affirmative
                      action" + US E-SIGN clickwrap requirements.
                      Test ID kept the same as the old age gate so prior
                      e2e tests pointing at `login-age-gate` keep finding
                      a target. */}
                  <p
                    className="hidden sm:block mt-4 text-xs text-[#5C4B51] leading-relaxed text-center"
                    data-testid="login-age-gate"
                  >
                    <Trans
                      i18nKey="login.inlineLegal"
                      components={{
                        terms: (
                          <Link
                            to="/terms"
                            target="_blank"
                            className="text-[#9B6A6C] underline hover:text-[#805557]"
                          />
                        ),
                        privacy: (
                          <Link
                            to="/privacy"
                            target="_blank"
                            className="text-[#9B6A6C] underline hover:text-[#805557]"
                          />
                        ),
                      }}
                    />
                  </p>

                  {/* Marketing opt-in — explicit, OFF by default.
                      Compact single-line layout that doesn't compete
                      visually with the legal copy. Default-off keeps
                      us GDPR-compliant ("opt-in only" for marketing
                      emails); the user must actively tick to consent. */}
                  <label
                    className="hidden sm:flex mt-3 items-start gap-2 cursor-pointer text-xs text-[#5C4B51] justify-center"
                    data-testid="login-marketing-row"
                  >
                    <input
                      type="checkbox"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 accent-[#9B6A6C] cursor-pointer"
                      data-testid="login-marketing-checkbox"
                    />
                    <span className="leading-snug max-w-[280px]">
                      {t("login.marketingShort")}
                    </span>
                  </label>

                  {/* Email options — hidden by default. The "Continue
                      with email" button above sets showEmailOptions=true
                      which expands the tabs + form here. */}
                  {showEmailOptions && (
                    <>
                      <div className="flex items-center gap-3 my-3 sm:my-6">
                        <div className="h-px bg-[#EADDD7] flex-1" />
                        <span className="text-xs uppercase tracking-[0.2em] text-[#8A7D82]">
                          {t("login.orWithEmail")}
                        </span>
                        <div className="h-px bg-[#EADDD7] flex-1" />
                      </div>

                      {/* Mode tabs */}
                      <div
                        className="flex gap-1 sm:gap-2 bg-[#FAF6F2] rounded-full p-1 mb-3 sm:mb-5"
                        data-testid="login-mode-tabs"
                      >
                        <ModeTab
                          id="password-signin"
                          label={t("login.tabSignIn")}
                          testid="login-tab-signin"
                        />
                        <ModeTab
                          id="password-signup"
                          label={t("login.tabSignUp")}
                          testid="login-tab-signup"
                        />
                        <ModeTab
                          id="magic-link"
                          label={t("login.tabMagicLink")}
                          testid="login-tab-magic"
                        />
                      </div>

                  {/* ── PASSWORD SIGN IN ─────────────────────────── */}
                  {authMode === "password-signin" && (
                    <form
                      onSubmit={handlePasswordSignIn}
                      className="space-y-3"
                      data-testid="login-password-signin-form"
                    >
                      <div className="relative">
                        <Mail className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t("login.emailPlaceholder")}
                          className={inputClass}
                          data-testid="login-email-input"
                        />
                      </div>
                      <p className="px-2 text-xs leading-relaxed text-[#76686D]">
                        {t("login.emailAliasHint")}
                      </p>
                      <div className="relative">
                        <Lock className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          required
                          minLength={8}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={t("login.passwordPlaceholder")}
                          className={`${inputClass} pr-10`}
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A7D82] hover:text-[#2C2123] p-1"
                          aria-label={showPassword ? t("login.hidePasswordA11y") : t("login.showPasswordA11y")}
                          data-testid="login-password-toggle"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {/* Iter 83 — "Forgot password?" promoted from
                          tiny under-submit link to inline right-
                          aligned link directly under the password
                          field. That's where users look when they
                          mistype their password on mobile (matches
                          iOS / Android native conventions). */}
                      <div className="flex justify-end -mt-1">
                        <Link
                          to="/auth/forgot-password"
                          className="text-sm font-medium text-[#9B6A6C] hover:text-[#805557] underline-offset-4 hover:underline"
                          data-testid="login-forgot-link"
                        >
                          {t("login.forgotEasy")}
                        </Link>
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-base bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-60 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 transition-all duration-300"
                        data-testid="login-signin-btn"
                      >
                        {submitting ? (
                          <>
                            <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            {t("login.signingIn")}
                          </>
                        ) : (
                          <>
                            {t("login.submitSignIn")}
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  {/* ── PASSWORD SIGN UP ─────────────────────────── */}
                  {authMode === "password-signup" && (
                    <form
                      onSubmit={handlePasswordSignUp}
                      className="space-y-3"
                      data-testid="login-password-signup-form"
                    >
                      <div className="relative">
                        <Sparkles className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          autoComplete="nickname"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder={t("login.namePlaceholder")}
                          maxLength={120}
                          className={inputClass}
                          data-testid="login-name-input"
                        />
                      </div>
                      <div className="relative">
                        <Mail className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t("login.emailPlaceholder")}
                          className={inputClass}
                          data-testid="login-email-input"
                        />
                      </div>
                      <p className="px-2 text-[11px] sm:text-xs leading-relaxed text-[#76686D]">
                        {t("login.identityPrivacyHint")}
                      </p>
                      <div className="relative">
                        <Lock className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          required
                          minLength={8}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={t("login.passwordPlaceholderSignup")}
                          aria-describedby="login-password-rules"
                          className={`${inputClass} pr-10`}
                          data-testid="login-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A7D82] hover:text-[#2C2123] p-1"
                          aria-label={showPassword ? t("login.hidePasswordA11y") : t("login.showPasswordA11y")}
                          data-testid="login-password-toggle"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      {/* Iter 83 — inline password rule. Renders BELOW
                          the field (not as a tooltip) so mobile users
                          who never hover see the requirement. Turns
                          green once the rule is met for a small
                          positive reinforcement. */}
                      <p
                        id="login-password-rules"
                        className={`text-xs flex items-center gap-1.5 -mt-1 mb-1 transition-colors ${
                          password.length >= 8 ? "text-[#5A8A6F]" : "text-[#8A7D82]"
                        }`}
                        data-testid="login-password-rules"
                      >
                        <span
                          aria-hidden="true"
                          className={`inline-block h-1.5 w-1.5 rounded-full ${
                            password.length >= 8 ? "bg-[#5A8A6F]" : "bg-[#D5A8A4]"
                          }`}
                        />
                        {t("login.passwordRulesShort")}
                      </p>
                      <label className="sm:hidden flex items-start gap-2 cursor-pointer text-[11px] text-[#5C4B51]">
                        <input
                          type="checkbox"
                          checked={marketingOptIn}
                          onChange={(e) => setMarketingOptIn(e.target.checked)}
                          className="mt-0.5 h-3.5 w-3.5 accent-[#9B6A6C] cursor-pointer"
                        />
                        <span className="leading-snug">{t("login.marketingShort")}</span>
                      </label>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-base bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-60 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 transition-all duration-300"
                        data-testid="login-signup-btn"
                      >
                        {submitting ? (
                          <>
                            <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            {t("login.creatingAccount")}
                          </>
                        ) : (
                          <>
                            {t("login.submitSignUp")}
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </form>
                  )}

                  {/* ── MAGIC LINK ────────────────────────────────── */}
                  {authMode === "magic-link" && (
                    <form
                      onSubmit={handleMagicLink}
                      className="space-y-3"
                      data-testid="login-magic-link-form"
                    >
                      <div className="relative">
                        <Sparkles className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          autoComplete="nickname"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder={t("login.namePlaceholder")}
                          maxLength={120}
                          disabled={view === "sending"}
                          className={inputClass}
                          data-testid="login-magic-name-input"
                        />
                      </div>
                      <div className="relative">
                        <Mail className="h-4 w-4 text-[#8A7D82] absolute left-4 top-1/2 -translate-y-1/2" />
                        <input
                          type="email"
                          autoComplete="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t("login.emailPlaceholder")}
                          disabled={view === "sending"}
                          className={inputClass}
                          data-testid="login-email-input"
                        />
                      </div>
                      <p className="px-2 text-[11px] sm:text-xs leading-relaxed text-[#76686D]">
                        {t("login.identityPrivacyHint")}
                      </p>
                      <label className="sm:hidden flex items-start gap-2 cursor-pointer text-[11px] text-[#5C4B51]">
                        <input
                          type="checkbox"
                          checked={marketingOptIn}
                          onChange={(e) => setMarketingOptIn(e.target.checked)}
                          className="mt-0.5 h-3.5 w-3.5 accent-[#9B6A6C] cursor-pointer"
                        />
                        <span className="leading-snug">{t("login.marketingShort")}</span>
                      </label>
                      <button
                        type="submit"
                        disabled={view === "sending"}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-base bg-[#9B6A6C] text-white hover:bg-[#805557] disabled:opacity-60 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 transition-all duration-300"
                        data-testid="login-magic-link-btn"
                      >
                        {view === "sending" ? (
                          <>
                            <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            {t("login.sendingMagicLink")}
                          </>
                        ) : (
                          <>
                            {t("login.sendMagicLink")}
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                    </>
                  )}
                  <p className="sm:hidden mt-3 text-[11px] text-[#5C4B51] leading-relaxed text-center">
                    <Trans
                      i18nKey="login.inlineLegal"
                      components={{
                        terms: <Link to="/terms" target="_blank" className="text-[#9B6A6C] underline" />,
                        privacy: <Link to="/privacy" target="_blank" className="text-[#9B6A6C] underline" />,
                      }}
                    />
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="mt-3 sm:mt-6 flex flex-col items-center gap-2 sm:gap-3">
            <p
              className="max-w-md text-center text-xs leading-relaxed text-[#6F6166]"
              data-testid="login-stealth-assurance"
            >
              {t("login.stealthAssurance")}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors"
              data-testid="login-back-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("login.backHome")}
            </Link>
          </div>
        </motion.div>
      </section>

      <BetaGatedModal
        open={betaGate.open}
        payload={betaGate.payload}
        prefilledEmail={email.trim().toLowerCase()}
        onClose={() => setBetaGate({ open: false, payload: null })}
      />
    </div>
  );
};

export default Login;
